import React, { useEffect, useState, useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Box,
  Typography,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { getFinancialColor } from '@/theme';
import { useAccountBookStore } from '@/store/accountBookStore';
import { getExpensesByUserFromShared } from '@/services';
import type { RecordDocument } from '@/services/firestoreService';
import SectionHeader from '../SectionHeader';

type Granularity = 'month' | 'week' | 'day';

interface ChartDatum {
  label: string;
  revenue: number;
  expense: number;
  netIncome: number;
  /** 주별 뷰: 기간 표시 (툴팁용) */
  range?: string;
}

const isIncomeRecord = (r: RecordDocument) =>
  r.incomeType === '매출' || r.incomeType === 'income';
const isExpenseRecord = (r: RecordDocument) =>
  r.incomeType === '지출' || r.incomeType === 'expense';

/** 해당 날짜가 속한 주의 월요일 00:00 */
function weekStartOf(d: Date): Date {
  const start = new Date(d);
  start.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  start.setHours(0, 0, 0, 0);
  return start;
}

const shortDate = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;

/** 연간 거래를 주(월요일 시작) 단위로 집계 — 빈 주도 0으로 채워 연속 표시 */
function aggregateWeekly(records: RecordDocument[], year: number): ChartDatum[] {
  const buckets = new Map<number, { revenue: number; expense: number }>();

  // 1/1이 속한 주부터 12/31이 속한 주까지 0으로 초기화
  const cursor = weekStartOf(new Date(year, 0, 1));
  const yearEnd = new Date(year, 11, 31);
  while (cursor <= yearEnd) {
    buckets.set(cursor.getTime(), { revenue: 0, expense: 0 });
    cursor.setDate(cursor.getDate() + 7);
  }

  records.forEach((r) => {
    const d = r.createdTime?.toDate?.();
    if (!d) return;
    const key = weekStartOf(d).getTime();
    const bucket = buckets.get(key);
    if (!bucket) return;
    const amount = Number(r.money) || 0;
    if (isIncomeRecord(r)) bucket.revenue += amount;
    else if (isExpenseRecord(r)) bucket.expense += amount;
  });

  return [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([time, v]) => {
      const start = new Date(time);
      const end = new Date(time);
      end.setDate(end.getDate() + 6);
      const weekOfMonth = Math.ceil(start.getDate() / 7);
      return {
        label: `${start.getMonth() + 1}월${weekOfMonth}주`,
        range: `${shortDate(start)} ~ ${shortDate(end)}`,
        revenue: v.revenue,
        expense: v.expense,
        netIncome: v.revenue - v.expense,
      };
    });
}

/** 선택한 달의 일별 집계 — 빈 날도 0으로 채움 */
function aggregateDaily(
  records: RecordDocument[],
  year: number,
  month: number
): ChartDatum[] {
  const daysInMonth = new Date(year, month, 0).getDate();
  const days = Array.from({ length: daysInMonth }, () => ({
    revenue: 0,
    expense: 0,
  }));

  records.forEach((r) => {
    const d = r.createdTime?.toDate?.();
    if (!d || d.getMonth() + 1 !== month) return;
    const amount = Number(r.money) || 0;
    const bucket = days[d.getDate() - 1];
    if (isIncomeRecord(r)) bucket.revenue += amount;
    else if (isExpenseRecord(r)) bucket.expense += amount;
  });

  return days.map((v, i) => ({
    label: `${i + 1}일`,
    revenue: v.revenue,
    expense: v.expense,
    netIncome: v.revenue - v.expense,
  }));
}

const RevenueExpenseChart: React.FC = () => {
  const theme = useTheme();
  const {
    dashboardData,
    userId,
    currentAccountNumber,
    currentAccountBook,
    isLoading,
  } = useAccountBookStore();

  const now = new Date();
  const [granularity, setGranularity] = useState<Granularity>('month');
  const [dayMonth, setDayMonth] = useState<number>(
    dashboardData.selectedYear === now.getFullYear() ? now.getMonth() + 1 : 1
  );

  // 주별/일별용 원본 거래 — 처음 전환할 때만 로드, (계정·연도)별 캐시
  const [rawRecords, setRawRecords] = useState<RecordDocument[]>([]);
  const [rawKey, setRawKey] = useState<string>('');
  const [rawLoading, setRawLoading] = useState(false);

  const accountNumber =
    currentAccountNumber || currentAccountBook?.accountNumber || 1;
  const cacheKey = `${userId}-${accountNumber}-${dashboardData.selectedYear}`;

  // 데이터 로딩은 Dashboard 페이지/store 초기화 흐름이 담당 — 여기서는 표시만 한다

  // 주별/일별 전환 시 원본 거래 지연 로드
  useEffect(() => {
    if (granularity === 'month' || !userId || rawKey === cacheKey) return;
    let cancelled = false;
    setRawLoading(true);
    getExpensesByUserFromShared(userId)
      .then((all: RecordDocument[]) => {
        if (cancelled) return;
        const filtered = all.filter((r) => {
          const d = r.createdTime?.toDate?.();
          return (
            r.accountNumber === accountNumber &&
            d?.getFullYear() === dashboardData.selectedYear
          );
        });
        setRawRecords(filtered);
        setRawKey(cacheKey);
      })
      .catch((e) => console.error('주별/일별 데이터 로딩 실패:', e))
      .finally(() => !cancelled && setRawLoading(false));
    return () => {
      cancelled = true;
    };
  }, [granularity, userId, cacheKey, rawKey, accountNumber, dashboardData.selectedYear]);

  const chartData: ChartDatum[] = useMemo(() => {
    if (granularity === 'month') {
      return dashboardData.monthlyData.map((item) => ({
        label: `${item.month}월`,
        revenue: item.income,
        expense: item.expense,
        netIncome: item.netIncome,
      }));
    }
    if (rawKey !== cacheKey) return [];
    return granularity === 'week'
      ? aggregateWeekly(rawRecords, dashboardData.selectedYear)
      : aggregateDaily(rawRecords, dashboardData.selectedYear, dayMonth);
  }, [granularity, dashboardData.monthlyData, dashboardData.selectedYear, rawRecords, rawKey, cacheKey, dayMonth]);

  const formatCurrency = (value: number) => value.toLocaleString('ko-KR');

  /** Y축 전용 축약 표기 — '12,000,000' 대신 '1,200만'/'1.2억' (축 공간 절약 + 한눈 파악) */
  const formatAxisCurrency = (value: number): string => {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 100_000_000) {
      const v = abs / 100_000_000;
      return `${sign}${v >= 10 ? Math.round(v) : Math.round(v * 10) / 10}억`;
    }
    if (abs >= 10_000) return `${sign}${Math.round(abs / 10_000).toLocaleString('ko-KR')}만`;
    return `${sign}${abs.toLocaleString('ko-KR')}`;
  };

  const granularityTitle =
    granularity === 'month'
      ? `${dashboardData.selectedYear}년 월별 수익/비용`
      : granularity === 'week'
        ? `${dashboardData.selectedYear}년 주별 수익/비용`
        : `${dashboardData.selectedYear}년 ${dayMonth}월 일별 수익/비용`;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const range = payload[0]?.payload?.range;
      return (
        <Box
          sx={{
            backgroundColor: 'var(--bg-base)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--r-12)',
            p: 2,
            boxShadow: 'var(--shadow-3)',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: range ? 0 : 1, fontWeight: 600 }}>
            {label}
          </Typography>
          {range && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'var(--fg-tertiary)' }}>
              {range}
            </Typography>
          )}
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {entry.name === 'revenue' ? '매출' : entry.name === 'expense' ? '지출' : '순이익'}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatCurrency(entry.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  const showLoading = isLoading || (granularity !== 'month' && rawLoading);

  return (
    <Box sx={{ height: '100%' }}>
      <SectionHeader
        eyebrow="추이"
        title={granularityTitle}
        subtitle="매출 막대 · 지출 라인 · 순이익 영역"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {granularity === 'day' && (
              <Select
                size="small"
                value={dayMonth}
                onChange={(e) => setDayMonth(Number(e.target.value))}
                sx={{ minWidth: 84 }}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {i + 1}월
                  </MenuItem>
                ))}
              </Select>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={granularity}
              onChange={(_, v) => v != null && setGranularity(v)}
            >
              <ToggleButton value="month">월별</ToggleButton>
              <ToggleButton value="week">주별</ToggleButton>
              <ToggleButton value="day">일별</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
      />

      {showLoading ? (
        <Box sx={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          color: 'text.secondary'
        }}>
          <CircularProgress size={24} />
          <Typography>데이터를 불러오는 중...</Typography>
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary'
        }}>
          <Typography>{dashboardData.selectedYear}년 데이터가 없습니다.</Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            {/* 가로선만 은은하게 — 레퍼런스식 미니멀 그리드 */}
            <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis
              dataKey="label"
              stroke={theme.palette.text.secondary}
              fontSize={12}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              stroke={theme.palette.text.secondary}
              fontSize={12}
              tickFormatter={formatAxisCurrency}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                paddingTop: 20,
                fontSize: '12px',
                lineHeight: '16px'
              }}
              formatter={(value) => (
                <span style={{
                  color: theme.palette.text.primary,
                  fontSize: '12px'
                }}>
                  {value === 'revenue' ? '매출' : value === 'expense' ? '지출' : '순이익'}
                </span>
              )}
            />

            {/* 매출 - 캡슐형 막대 (Quixotic 라운드 바) */}
            <Bar
              dataKey="revenue"
              name="revenue"
              fill={getFinancialColor('income')}
              opacity={0.85}
              radius={[10, 10, 10, 10]}
              maxBarSize={28}
            />

            {/* 지출 - 선 차트 */}
            <Line
              type="monotone"
              dataKey="expense"
              name="expense"
              stroke={getFinancialColor('expense')}
              strokeWidth={3}
              dot={{ fill: getFinancialColor('expense'), strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: getFinancialColor('expense'), strokeWidth: 2 }}
            />

            {/* 순이익 - 영역 차트 (딥 그린 — 검정 대비 시각적 무게 완화) */}
            <Area
              type="monotone"
              dataKey="netIncome"
              name="netIncome"
              fill={getFinancialColor('profit')}
              stroke={getFinancialColor('profit')}
              strokeWidth={2}
              fillOpacity={0.12}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default RevenueExpenseChart;
